import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import withAuth from "../../utils/withAuth";
import { fetchCallAllDataByIDAPI } from "../../utils/api";
import ContentWrapper from "../../components/contentwrapper/ContentWrapper";
import ReportNavBar from "../../components/ReportNavBar";
import { numberFormatWithGst } from "../../components/numberFormatWithGst";

const SKELETON_COUNT = 10;

const SkeletonTableCard = () => (
    <div className="col-md-4 col-lg-3 col-sm-6 mb-4">
        <div className="card h-100 shadow-sm">
            <div className="card-body p-2">
                <div className="skeleton" style={{ height: 18, width: "70%", marginBottom: 10 }} />
                <div className="skeleton" style={{ height: 14, width: "100%", marginBottom: 6 }} />
                <div className="skeleton" style={{ height: 14, width: "100%", marginBottom: 6 }} />
                <div className="skeleton" style={{ height: 14, width: "80%", marginBottom: 6 }} />
            </div>
        </div>
    </div>
);

const perCallCost = (spend, calls) => {
    const c = Number(calls || 0);
    const s = Number(spend || 0);
    if (c <= 0) return 0;
    return s / c;
};

const TodayTeamCall = () => {
    const [process, setProcess] = useState([]);   // rows
    const [summary, setSummary] = useState(null); // summary
    const [isLoading, setIsLoading] = useState(false);

    // ✅ Date range filters (default today)
    const [fromDate, setFromDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [appliedDate, setAppliedDate] = useState({ from: "", to: "" });

    // ✅ Skill search
    const [skillQuery, setSkillQuery] = useState("");
    const [appliedSkillQuery, setAppliedSkillQuery] = useState("");

    // ✅ Team multi-select (checkbox dropdown)
    const [teamOpen, setTeamOpen] = useState(false);
    const [teamSearch, setTeamSearch] = useState("");
    const [selectedTeams, setSelectedTeams] = useState(new Set()); // working
    const [appliedTeams, setAppliedTeams] = useState(new Set());  // applied

    const [showGroups, setShowGroups] = useState(true);

    const navigate = useNavigate();
    const ppcid = localStorage.getItem("ppcdata");

    useEffect(() => {
        if (!ppcid) return navigate("/");
        navigate("/today-team-report");

        // ✅ default load = today
        setAppliedDate({ from: fromDate, to: toDate });
        getLiveCallWithProcessName(ppcid, fromDate, toDate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ppcid]);

    const getLiveCallWithProcessName = async (id, d1, d2) => {
        setIsLoading(true);
        try {
            const qs = new URLSearchParams();
            if (d1) qs.set("start_date", d1);
            if (d2) qs.set("end_date", d2);

            const res = await fetchCallAllDataByIDAPI(
                `ppc-team-get--today-live-call-with-proccess-name-by-id/${id}?${qs.toString()}`
            );

            if (res?.data?.status === 1) {
                const p = res?.data?.process || {};
                setProcess(Array.isArray(p?.rows) ? p.rows : []);
                setSummary(p?.summary || null);
            } else {
                toast.error(res?.data?.message || "Failed to load process");
                setProcess([]);
                setSummary(null);
            }
        } catch (e) {
            console.error(e);
            toast.error("Network/CORS error while loading process");
            setProcess([]);
            setSummary(null);
        } finally {
            setIsLoading(false);
        }
    };

    // ✅ TEAM mapping (updated like previous big map style + your team codes)
    const TEAM_BY_CODE = {
        // Team single-letter codes
        K: "Vivek Kumar",
        R: "Pragya Singh",
        J: "Akash Deep",
        V: "Pradeep Verma",
        T: "Yanshi",
        N: "Deepika Sisodia",
        D: "Deepak Kumar Giri",
        S: "Sumit Saini",
        I: "Ranjan Kumar",
        B: "Navin Chaurasia",
        E: "Bhumika Gola",
        G: "Vimal Jadon",
        P: "Anurodh Baral",
        M: "Simrati Kaur",
        Z: "Manprit Sir",
        A: "Ankita",
        W: "Ishika Pokhriyal",
        U: "Om Kumar",
        L: "Navin Chaurasia",
        Q: "Akash Deep",
    };

    const deriveTeamName = (skillRaw) => {
        const skill = String(skillRaw || "").trim();
        if (!skill) return "Other";

        const parts = skill.split("_").filter(Boolean);
        const n = parts.length;

        const last = (parts[n - 1] || "").toUpperCase();
        const last2 = n >= 2 ? `${(parts[n - 2] || "").toUpperCase()}_${last}` : null;
        const first = (parts[0] || "").toUpperCase();

        // ✅ Only if exists in TEAM_BY_CODE
        if (last2 && TEAM_BY_CODE[last2]) return TEAM_BY_CODE[last2];
        if (TEAM_BY_CODE[last]) return TEAM_BY_CODE[last];
        if (TEAM_BY_CODE[first]) return TEAM_BY_CODE[first];

        // ✅ do NOT return raw AM/D2C/etc
        return "Other";
    };


    const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
    
    // ✅ Group raw data
    const groupedDataRaw = useMemo(() => {
        const groups = {};
        (process || []).forEach((row) => {
            const code = String(row?.skillname || "");
            const calls = Number(row?.unique_calls || 0);
            const orders = Number(row?.order_count || 0);
            const spend = Number(row?.spend_today || 0);

            const teamName = deriveTeamName(code);

            if (!groups[teamName]) {
                groups[teamName] = { name: teamName, totalCalls: 0, totalOrders: 0, totalSpend: 0, items: [] };
            }

            groups[teamName].totalCalls += calls;
            groups[teamName].totalOrders += orders;
            groups[teamName].totalSpend += spend;

            groups[teamName].items.push({
                code,
                calls,
                orders,
                spend,
                perCall: perCallCost(spend, calls),
                perf: calls ? (orders / calls) * 100 : 0,
            });
        });

        Object.values(groups).forEach((g) => {
            g.perf = g.totalCalls ? (g.totalOrders / g.totalCalls) * 100 : 0;
            g.perCall = perCallCost(g.totalSpend, g.totalCalls);
            g.items.sort((a, b) => (b.calls !== a.calls ? b.calls - a.calls : a.code.localeCompare(b.code)));
        });

        return Object.values(groups).sort((a, b) =>
            b.totalCalls !== a.totalCalls ? b.totalCalls - a.totalCalls : a.name.localeCompare(b.name)
        );
    }, [process]);

    // ✅ Team options (checkbox list)
    const teamOptions = useMemo(() => {
        return Array.from(new Set(groupedDataRaw.map((g) => g.name).filter(Boolean))).sort((a, b) => a.localeCompare(b));
    }, [groupedDataRaw]);

    const filteredTeamOptions = useMemo(() => {
        const q = String(teamSearch || "").trim().toLowerCase();
        if (!q) return teamOptions;
        return teamOptions.filter((t) => t.toLowerCase().includes(q));
    }, [teamOptions, teamSearch]);

    // ✅ init selection to ALL
    useEffect(() => {
        if (!teamOptions.length) return;
        setSelectedTeams((prev) => (prev && prev.size ? prev : new Set(teamOptions)));
        setAppliedTeams((prev) => (prev && prev.size ? prev : new Set(teamOptions)));
    }, [teamOptions]);

    const toggleTeam = (name) => {
        setSelectedTeams((prev) => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name);
            else next.add(name);
            return next;
        });
    };

    const onSelectAllTeams = () => setSelectedTeams(new Set(teamOptions));
    const onClearTeams = () => setSelectedTeams(new Set());
    const onApplyTeams = () => {
        if (!selectedTeams.size) {
            const all = new Set(teamOptions);
            setSelectedTeams(all);
            setAppliedTeams(all);
            toast.info("No team selected, showing All Teams");
            setTeamOpen(false);
            return;
        }
        setAppliedTeams(new Set(selectedTeams));
        setTeamOpen(false);
    };
    const onResetTeams = () => {
        const all = new Set(teamOptions);
        setSelectedTeams(all);
        setAppliedTeams(all);
        setTeamSearch("");
        setTeamOpen(false);
    };

    const teamButtonText = useMemo(() => {
        if (!appliedTeams || appliedTeams.size === 0) return "All Teams";
        if (appliedTeams.size === teamOptions.length) return "All Teams";
        if (appliedTeams.size === 1) return Array.from(appliedTeams)[0];
        return `${appliedTeams.size} Teams`;
    }, [appliedTeams, teamOptions]);

    // ✅ apply Team + Skill filter on grouped data
    const groupedData = useMemo(() => {
        const q = String(appliedSkillQuery || "").trim().toLowerCase();
        const teams = appliedTeams && appliedTeams.size ? appliedTeams : new Set(teamOptions);

        let list = groupedDataRaw.filter((g) => teams.has(g.name));

        if (q) {
            list = list
                .map((g) => {
                    const items = (g.items || []).filter((it) => String(it.code || "").toLowerCase().includes(q));
                    if (!items.length) return null;

                    const totalCalls = items.reduce((s, it) => s + (Number(it.calls) || 0), 0);
                    const totalOrders = items.reduce((s, it) => s + (Number(it.orders) || 0), 0);
                    const totalSpend = items.reduce((s, it) => s + (Number(it.spend) || 0), 0);

                    return {
                        ...g,
                        items,
                        totalCalls,
                        totalOrders,
                        totalSpend,
                        perf: totalCalls ? (totalOrders / totalCalls) * 100 : 0,
                        perCall: perCallCost(totalSpend, totalCalls),
                    };
                })
                .filter(Boolean);
        }

        return list;
    }, [groupedDataRaw, appliedTeams, teamOptions, appliedSkillQuery]);

    // ✅ Prefer backend summary
    const totalCalls = useMemo(() => {
        if (summary && typeof summary.total_unique_calls !== "undefined") return Number(summary.total_unique_calls) || 0;
        return (process || []).reduce((sum, r) => sum + (Number(r?.unique_calls) || 0), 0);
    }, [process, summary]);

    const totalOrders = useMemo(() => {
        if (summary && typeof summary.total_orders_created !== "undefined") return Number(summary.total_orders_created) || 0;
        return (process || []).reduce((sum, r) => sum + (Number(r?.order_count) || 0), 0);
    }, [process, summary]);

    const totalSpend = useMemo(() => {
        if (summary && typeof summary.total_spend_today !== "undefined") return Number(summary.total_spend_today) || 0;
        return (process || []).reduce((sum, r) => sum + (Number(r?.spend_today) || 0), 0);
    }, [process, summary]);

    const perf = useMemo(() => (totalCalls ? (totalOrders / totalCalls) * 100 : 0), [totalCalls, totalOrders]);
    const perCall = useMemo(() => perCallCost(totalSpend, totalCalls), [totalSpend, totalCalls]);

    // ✅ Apply filters (date + team + skill)
    const onApplyFilters = () => {
        const d1 = String(fromDate || "");
        const d2 = String(toDate || "");
        if (!d1 || !d2) return toast.error("Please select From Date and To Date");
        if (d1 > d2) return toast.error("From Date cannot be greater than To Date");

        setAppliedDate({ from: d1, to: d2 });
        setAppliedSkillQuery(skillQuery || "");
        setAppliedTeams(new Set(selectedTeams.size ? selectedTeams : teamOptions));
        setTeamOpen(false);

        if (ppcid) getLiveCallWithProcessName(ppcid, d1, d2);
    };

    const onResetFilters = () => {
        const today = new Date().toISOString().slice(0, 10);
        setFromDate(today);
        setToDate(today);
        setSkillQuery("");
        setAppliedSkillQuery("");

        const all = new Set(teamOptions);
        setSelectedTeams(all);
        setAppliedTeams(all);

        setAppliedDate({ from: today, to: today });
        setTeamSearch("");
        setTeamOpen(false);

        if (ppcid) getLiveCallWithProcessName(ppcid, today, today);
    };

    const headerTitle = useMemo(() => {
        const d1 = appliedDate.from || fromDate;
        const d2 = appliedDate.to || toDate;
        if (d1 && d2) return `Today Team (${d1} → ${d2})`;
        return "Today Team";
    }, [appliedDate, fromDate, toDate]);

    return (
        <div className="misDashboard">
            <Helmet>
                <title>SATKARTAR::DASHBOARD</title>
            </Helmet>

            <style>{`
        .skeleton{position:relative;overflow:hidden;background:#eee;border-radius:6px}
        .skeleton:after{content:"";position:absolute;inset:0;transform:translateX(-100%);
          background:linear-gradient(90deg,transparent,rgba(255,255,255,.6),transparent);
          animation:shimmer 1.2s infinite}
        @keyframes shimmer{100%{transform:translateX(100%)}}

        /* ✅ show full skill name in ONE LINE (no dot-dot) */
        .mini-table{ width:100%; }
        .mini-table th,.mini-table td{font-size:12px; padding:6px 8px; vertical-align:middle;}
        .skillCell{
          white-space:nowrap !important;
          overflow:visible !important;
          text-overflow:unset !important;
        }

        /* sticky header inside card scroll */
        .stickyHead { position: sticky; top: 0; z-index: 2; background: #fff; }
        .skillStickyHeader { background:#fff; }
        .colHead th{ background:#fafafa; }

        .filterRow{display:flex;gap:8px;align-items:end;flex-wrap:wrap;}
        .filterBox{display:flex;flex-direction:column;gap:4px; position:relative;}
        .filterBox label{font-weight:600;font-size:12px;margin:0;}

        /* ✅ Team checkbox dropdown (renamed from center) */
        .teamDropWrap{position:relative; min-width:260px;}
        .teamDropBtn{
          width:260px;
          display:flex; align-items:center; justify-content:space-between;
          padding:6px 10px;
          border:1px solid rgba(0,0,0,.15);
          border-radius:6px;
          background:#fff;
          font-size:13px;
        }
        .teamDropMenu{
          position:absolute; top:100%; left:0; z-index:50;
          width:260px;
          background:#fff;
          border:1px solid rgba(0,0,0,.15);
          border-radius:10px;
          margin-top:6px;
          box-shadow:0 10px 30px rgba(0,0,0,.12);
          overflow:hidden;
        }
        .teamDropHead{padding:10px; border-bottom:1px solid rgba(0,0,0,.08);}
        .teamSearch{
          width:100%;
          border:1px solid rgba(0,0,0,.15);
          border-radius:8px;
          padding:7px 10px;
          font-size:13px;
          outline:none;
        }
        .teamActions{
          display:flex; padding:10px;
          border-bottom:1px solid rgba(0,0,0,.08);
          justify-content:space-between;
        }
        .teamList{max-height:240px; overflow:auto; padding:8px 5px;}
        .teamItem{display:flex; align-items:center; gap:10px; padding:6px 2px; font-size:13px;}
        .teamFoot{
          display:flex; gap:8px; justify-content:space-between;
          padding:10px;
          border-top:1px solid rgba(0,0,0,.08);
          background:#fafafa;
        }
      `}</style>

            <ContentWrapper>
                <div className="row">
                    <div className="col-12">
                        <ReportNavBar />
                    </div>
                </div>

                {/* ✅ Top Summary */}
                <div className="">
                    <h5 className="livecallcount">
                        {headerTitle}
                        {"  |  "}
                        Calls: {isLoading ? "..." : fmt(totalCalls)}
                        {"  |  "}
                        Orders: {isLoading ? "..." : fmt(totalOrders)}
                        {"  |  "}
                        Spend: {isLoading ? "..." : `${numberFormatWithGst(totalSpend)}`}
                        {"  |  "}
                        Cost:{" "}
                        <span style={{ color: perCall > 100 ? "#d32f2f" : "#111", fontWeight: 700 }}>
                            {isLoading ? "..." : `${numberFormatWithGst(perCall)}`}
                        </span>
                        {"  |  "}
                        Perf: {isLoading ? "..." : `${perf.toFixed(1)}%`}
                    </h5>
                </div>

                {/* ✅ Filters */}
                <div className="card shadow-sm mt-2">
                    <div className="card-body p-2">
                        <div className="filterRow">
                            <div className="filterBox">
                                <label>From Date</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>

                            <div className="filterBox">
                                <label>To Date</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>

                            {/* ✅ Team checkbox dropdown */}
                            <div className="filterBox">
                                <label>Team Name</label>
                                <div className="teamDropWrap">
                                    <button
                                        type="button"
                                        className="teamDropBtn"
                                        onClick={() => setTeamOpen((s) => !s)}
                                        disabled={isLoading || !teamOptions.length}
                                    >
                                        <span style={{ fontWeight: 600 }}>{teamButtonText}</span>
                                        <span style={{ opacity: 0.7 }}>▾</span>
                                    </button>

                                    {teamOpen && (
                                        <div className="teamDropMenu">
                                            <div className="teamDropHead">
                                                <input
                                                    className="teamSearch"
                                                    value={teamSearch}
                                                    onChange={(e) => setTeamSearch(e.target.value)}
                                                    placeholder="Search team..."
                                                />
                                            </div>

                                            <div className="teamActions">
                                                <button type="button" className="btnselectall" onClick={onSelectAllTeams}>
                                                    Select All
                                                </button>
                                                <button type="button" className="btnresetall" onClick={onClearTeams}>
                                                    Clear
                                                </button>
                                            </div>

                                            <div className="teamList">
                                                {filteredTeamOptions.length ? (
                                                    filteredTeamOptions.map((name) => (
                                                        <label key={name} className="teamItem">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedTeams.has(name)}
                                                                onChange={() => toggleTeam(name)}
                                                            />
                                                            <span>{name}</span>
                                                        </label>
                                                    ))
                                                ) : (
                                                    <div className="text-muted" style={{ fontSize: 13, padding: "8px 2px" }}>
                                                        No teams found
                                                    </div>
                                                )}
                                            </div>

                                            <div className="teamFoot">
                                                <button type="button" className="btnselectall" onClick={onResetTeams}>
                                                    Reset All
                                                </button>
                                                <button type="button" className="btnresetall" onClick={onApplyTeams}>
                                                    Apply Teams
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* ✅ Skill search */}
                            <div className="filterBox">
                                <label>Search Skill</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Search skill..."
                                    value={skillQuery}
                                    onChange={(e) => setSkillQuery(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>

                            <button className="btnskillapply" onClick={onApplyFilters} disabled={isLoading}>
                                Apply
                            </button>

                            <button className="btnresetall" onClick={onResetFilters} disabled={isLoading}>
                                Reset
                            </button>

                            <button className="btnskill" onClick={() => setShowGroups((s) => !s)} disabled={isLoading}>
                                {showGroups ? "Hide Team View" : "Show Team View"}
                            </button>
                        </div>
                    </div>
                </div>

                {/* ✅ Cards */}
                {showGroups && (
                    <div className="row mt-3 livecallbody">
                        {isLoading
                            ? Array.from({ length: SKELETON_COUNT }).map((_, i) => <SkeletonTableCard key={`sk-${i}`} />)
                            : groupedData.map((group, idx) => (
                                <div className="col-md-6 col-lg-4 mb-3" key={idx}>
                                    <div className="card shadow-sm">
                                        <div className="card-body p-2">
                                            <div className="p-0" style={{ maxHeight: 260, overflowY: "auto" }}>
                                                <table className="table table-sm table-bordered mb-0 mini-table">
                                                    <thead className="stickyHead">
                                                        <tr>
                                                            <th colSpan={6} className="skillStickyHeader">
                                                                <div className="d-flex justify-content-between">
                                                                    <div className="sh-left">{group.name}</div>
                                                                    <div className="sh-right">
                                                                        <span>
                                                                            Calls: <b>{fmt(group.totalCalls)}</b>
                                                                        </span>{" "}
                                                                        <span>
                                                                            | Orders: <b>{fmt(group.totalOrders)}</b>
                                                                        </span>{" "}
                                                                        <span>| {numberFormatWithGst(group.totalSpend)}</span>{" "}
                                                                        <span>
                                                                            | Cost:{" "}
                                                                            <b style={{ color: group.perCall > 100 ? "#d32f2f" : "#111" }}>
                                                                                {numberFormatWithGst(group.perCall)}
                                                                            </b>
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </th>
                                                        </tr>

                                                        <tr className="colHead">
                                                            <th>Skill</th>
                                                            <th style={{ width: 40, textAlign: "right" }}>Calls</th>
                                                            <th style={{ width: 40, textAlign: "right" }}>Orders</th>
                                                            <th style={{ width: 70, textAlign: "right" }}>Spend</th>
                                                            <th style={{ width: 70, textAlign: "right" }}>Cost</th>
                                                            <th style={{ width: 55, textAlign: "right" }}>Perf</th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {group.items.map((item, ix) => {
                                                            const isGood = item.calls > 0 && item.perf >= 15;
                                                            const perfColor = item.calls === 0 ? "#999" : isGood ? "#2e7d32" : "#d32f2f";
                                                            const arrow = item.calls === 0 ? "—" : isGood ? "↑" : "↓";
                                                            const perCallColor = item.perCall > 100 ? "#d32f2f" : "#111";

                                                            return (
                                                                <tr key={ix}>
                                                                    <td className="skillCell">{item.code}</td>
                                                                    <td style={{ textAlign: "right" }}>{fmt(item.calls)}</td>
                                                                    <td style={{ textAlign: "right" }}>{fmt(item.orders)}</td>
                                                                    <td style={{ textAlign: "right" }}>{numberFormatWithGst(item.spend)}</td>
                                                                    <td style={{ textAlign: "right", color: perCallColor }}>
                                                                        {numberFormatWithGst(item.perCall)}
                                                                    </td>
                                                                    <td style={{ textAlign: "right", fontWeight: 800, color: perfColor, display: "flex" }}>
                                                                        <span style={{ display: "inline-block", minWidth: 14, textAlign: "center" }}>
                                                                            {arrow}
                                                                        </span>{" "}
                                                                        <span>{item.calls === 0 ? "0.0%" : `${item.perf.toFixed(1)}%`}</span>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}

                        {!isLoading && groupedData.length === 0 && (
                            <div className="col-12">
                                <div className="alert alert-info mb-0">No data for selected filters.</div>
                            </div>
                        )}
                    </div>
                )}
            </ContentWrapper>
        </div>
    );
};

export default withAuth(TodayTeamCall);
